/**
 * B8 voxel assembly — reverse-direction dissolve for unit deploy /
 * match-open / respawn moments. Same per-voxel hash-threshold shader
 * as `killChoreography` (we reuse the dissolve patch on the chassis
 * material), but uProgress runs 1 → 0 over the assembly window with
 * an exponential ease so the chassis "snaps in" near the end.
 *
 * Pure math; the caller mutates `material.userData.dissolveUniforms
 * .uProgress.value` from a useFrame loop.
 */

export const ASSEMBLY_DURATION_MS = 850;
/** Initial pause where the chassis is fully absent. */
const PRE_HOLD_MS = 100;

export interface AssemblyState {
  /** Dissolve uProgress 0..1. 1 = fully scattered, 0 = fully assembled. */
  progress: number;
  /** True while the assembly is still mid-window — caller can skip
   *  other systems (walk bob, recoil) until done. */
  active: boolean;
}

const ASSEMBLED: AssemblyState = { progress: 0, active: false };

/**
 * Returns the assembly state given an elapsedMs since the rising edge.
 * Pass `null` when no assembly is in progress.
 */
export function assemblyState(elapsedMs: number | null): AssemblyState {
  if (elapsedMs === null || elapsedMs < 0) return ASSEMBLED;
  if (elapsedMs < PRE_HOLD_MS) {
    return { progress: 1, active: true };
  }
  const animSpan = ASSEMBLY_DURATION_MS - PRE_HOLD_MS;
  const t = (elapsedMs - PRE_HOLD_MS) / animSpan;
  if (t >= 1) return ASSEMBLED;
  // Exponential ease — slow start (chassis stays mostly scattered),
  // accelerating snap-in near the end.
  const progress = 1 - Math.pow(t, 3);
  return { progress, active: true };
}
